import UsersClient from '../../clients/UsersClient';
import { AppThunkAction } from '../reduxTypes';
import {
  LOAD_USERS_REQUEST,
  LOAD_USERS_SUCCESS,
  LOAD_USERS_FAILURE,
  ADD_USER_REQUEST,
  ADD_USER_SUCCESS,
  ADD_USER_FAILURE,
  SET_USER_ROLE_REQUEST,
  SET_USER_ROLE_SUCCESS,
  SET_USER_ROLE_FAILURE,
  DELETE_USER_FAILED,
  DELETE_USER_REQUEST,
  DELETE_USER_SUCCESS,
  SET_INTERNAL_USERS,
  ADD_INTERNAL_USERS,
  MODIFY_INTERNAL_USERS,
  REMOVE_INTERNAL_USERS,
  INVITE_USER_REQUEST,
  INVITE_USER_SUCCESS,
  INVITE_USER_FAILURE,
} from './types';
import {
  Client,
  ClientFormData,
  LOAD_ASSIGNED_CLIENTS,
} from '../clients/types';
import { User, UserRolesTypesUnMapped } from '../../constants/dataTypes';
import ApiUtils from '../../utils/ApiUtils';
import { CallApiWithNotification } from '../../clients/ApiService';
import ClientsClient from '../../clients/ClientsClient';

/**
 * Set the internal users from initial data load
 */
export const setInternalUsers = (users: User[]) => ({
  type: SET_INTERNAL_USERS,
  payload: users,
});

export const addInternalUsers = (users: User[]) => ({
  type: ADD_INTERNAL_USERS,
  payload: users,
});

/**
 * Modify internal users in state. If this includes the current user
 * also updated clients state to change assignedClients. Because
 * the current users assignees could have been added/removed
 * @param users list of internal users to modify in state
 * @returns
 */
export const modifyInternalUsers = (users: User[]): AppThunkAction => async (
  dispatch,
  getState,
) => {
  const currentUserID = getState().user.id;
  const { activeClients } = getState().clients;
  const currentInternalUser = users.find(
    u => u.cognitoUsername === currentUserID,
  );
  if (currentInternalUser) {
    // if the current user is in the list of modified users, dispatch separate action to update clients
    const assignedCompanyIDSet = {
      ...(currentInternalUser.additionalFields?.assignedToCompanies || {}),
      ...(currentInternalUser.additionalFields?.leadOfCompanies || {}),
    };
    // filter out any clients that are not part of the set of assigned company IDs
    const assignedClients = activeClients.filter(c =>
      Boolean(c.fields?.companyId && assignedCompanyIDSet[c.fields?.companyId]),
    );
    dispatch({
      type: LOAD_ASSIGNED_CLIENTS,
      assignedClients,
    });
  }

  dispatch({ type: MODIFY_INTERNAL_USERS, payload: users });
};

export const removeInternalUsers = (users: User[]) => ({
  type: REMOVE_INTERNAL_USERS,
  payload: users,
});

export const SetUsersAction = (users: User[]) => ({
  type: LOAD_USERS_SUCCESS,
  payload: users,
});

export const inviteInternalUser = (
  userId: string,
  userFirstName: string,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: INVITE_USER_REQUEST };
  }

  function success(invitedUserId: string, invitedByUserId: string) {
    return { type: INVITE_USER_SUCCESS, invitedUserId, invitedByUserId };
  }

  function failure(error: string) {
    return { type: INVITE_USER_FAILURE, error };
  }

  dispatch(request());

  const result = await CallApiWithNotification({
    executeFunction: ClientsClient.inviteInternalUser,
    params: userId,
    successMessage: `${userFirstName} was sent an email to activate their account.`,
    errorMessage: `We could not invite ${userFirstName}. Support has been notified to investigate the issue.`,
    dispatch,
  });

  if (result.status) {
    dispatch(success(userId, result.data.fields.invitedBy));
  } else {
    dispatch(failure(result.data));
  }
};

export const listUsers = (
  isClient = false,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: LOAD_USERS_REQUEST };
  }

  function failure(error: string) {
    return { type: LOAD_USERS_FAILURE, error };
  }

  dispatch(request());

  const result = await CallApiWithNotification({
    executeFunction: isClient ? UsersClient.listClients : UsersClient.listUsers,
    params: {},
    successMessage: ``,
    errorMessage: `Can't load users`,
    dispatch,
  });

  if (result.status) {
    dispatch(SetUsersAction(result.data));
  } else {
    dispatch(failure(result.data));
  }
};

export const addUser = (
  clientData: ClientFormData,
  isClient?: boolean,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: ADD_USER_REQUEST };
  }

  function success(payload: User) {
    return { type: ADD_USER_SUCCESS, payload };
  }

  function failure(error: string) {
    return { type: ADD_USER_FAILURE, error };
  }

  dispatch(request());

  const result = await CallApiWithNotification({
    executeFunction: UsersClient.addUser,
    checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
    params: { clientData, isClient },
    successMessage: `${clientData.cognitoFirstName} was sent their invitation to join the team.`,
    errorMessage: `We were unable to invite ${clientData.cognitoFirstName}. Support has been notified to investigate the issue.`,
    dispatch,
  });

  if (result.status) {
    const newClient = result.data.createdItems[0];
    dispatch(success(newClient));
  } else {
    dispatch(failure(result.data));
  }
};

export const setUserRole = (
  clientData: Client,
  roles: Array<UserRolesTypesUnMapped>,
): AppThunkAction => async (dispatch, getState) => {
  const { user } = getState();

  function request() {
    return { type: SET_USER_ROLE_REQUEST };
  }

  function success(
    updatedUserId: string,
    newRoles: Array<UserRolesTypesUnMapped>,
  ) {
    return { type: SET_USER_ROLE_SUCCESS, payload: updatedUserId, newRoles };
  }

  function failure(error: string) {
    return { type: SET_USER_ROLE_FAILURE, error };
  }

  dispatch(request());

  const result = await CallApiWithNotification({
    executeFunction: UsersClient.setUserRole,
    checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
    params: {
      user: clientData,
      isClient: user.isClient,
      roles,
    },
    successMessage: `The role has been changed.`,
    errorMessage: `We were unable to set the role. Support has been notified to investigate the issue.`,
    dispatch,
  });

  if (result.status) {
    const updatedUserId = result.data.succeededIds[0];
    dispatch(success(updatedUserId, roles));
  } else {
    dispatch(failure(result.data));
  }
};

export const deleteUser = (
  user: User,
  userType: 'internal' | 'client' = 'internal',
): AppThunkAction => async dispatch => {
  function request() {
    return { type: DELETE_USER_REQUEST };
  }

  function success(payload: string) {
    return { type: DELETE_USER_SUCCESS, payload };
  }

  function failure(error: string) {
    return { type: DELETE_USER_FAILED, error };
  }

  dispatch(request());

  const result = await CallApiWithNotification({
    executeFunction:
      userType === 'internal'
        ? UsersClient.deleteUser
        : ClientsClient.deleteUser,
    checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
    params: user,
    successMessage: `${
      user ? `${user.fields.given_name} ${user.fields.family_name}` : ''
    } has been deleted.`,
    errorMessage: `${
      user ? `${user.fields.given_name} ${user.fields.family_name}` : ''
    } could not be deleted. Support has been notified to investigate the issue.`,
    dispatch,
  });

  if (result.status) {
    dispatch(success(user.id));
  } else {
    dispatch(failure(result.data));
  }
};
